import React, { useMemo, useState } from 'react';
import { ICONS, S3BucketConfig } from 'src/common/constants';
import Document from '../../Document';
import FontAwesomeIcon from '../../Icon';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { isImage } from 'src/helpers/CommonHelpers';
import { Attachments } from 'src/pages/ServiceRequests/components/Attachments';
import { ServiceRequestDocument } from 'src/SharedCustomHooks/UseNotesAPI/types';
import {
  buildDocumentAttachmentPreview,
  EnrichedServiceRequestDocument,
} from './helpers';
import './DocumentsRepeater.scss';

interface DocumentsRepeaterProps {
  documents: ServiceRequestDocument[];
  onDeleteDocument?: (document: any) => void;
  onDownload: (image?: string) => void;
  onClick: (index: number, imageOrDocument: string, isImage: boolean) => void;
  user: any;
  from?: string;
  useCustomConfirmation?: boolean;
  canDelete?: boolean;
}

const DocumentsRepeater = ({
  documents,
  onDeleteDocument,
  onDownload,
  onClick,
  user,
  from,
  useCustomConfirmation,
  canDelete,
}: DocumentsRepeaterProps) => {
  const role: string = user?.roles?.[0]?.role?.name ?? '';
  const [documentToDelete, setDocumentToDelete] = useState<any | null>(null);

  const enrichedDocuments: EnrichedServiceRequestDocument[] = useMemo(
    () =>
      documents.map(
        (doc: ServiceRequestDocument): EnrichedServiceRequestDocument => ({
          ...doc,
          attachmentPreview: buildDocumentAttachmentPreview(doc),
        })
      ),
    [documents]
  );

  const handleDelete = (document: any): void => {
    if (!onDeleteDocument) return;

    if (useCustomConfirmation) {
      onDeleteDocument(document);
      return;
    }

    setDocumentToDelete(document);
  };

  const handleConfirmDelete = (): void => {
    if (documentToDelete && onDeleteDocument) {
      onDeleteDocument(documentToDelete);
    }

    setDocumentToDelete(null);
  };

  const handleCancelDelete = (): void => {
    setDocumentToDelete(null);
  };

  const effectiveCanDelete: boolean =
    canDelete !== undefined
      ? canDelete
      : role !== 'Strata Manager' &&
        role !== 'Home Owner' &&
        role !== 'Realtor' &&
        from !== 'keypickup';

  return (
    <>
    <div className="docs-repeater-scroll">
    <div className="docs-repeater-grid">
      {enrichedDocuments.map(
        (doc: EnrichedServiceRequestDocument, index: number) => (
          <div className="docs-repeater-cell" key={`DR-${from ?? ''}-${index}`}>
            {doc.Embed && doc.Embed.length > 0 ? (
              <>
                {effectiveCanDelete && (
                  <div className="docs-repeater-embed-delete">
                    <div onClick={() => handleDelete(doc)}>
                      <FontAwesomeIcon
                        glyph="times-circle"
                        weight="regular"
                        size="small"
                      />
                    </div>
                  </div>
                )}
                <div className="docs-repeater-embed-card">
                  <div className="docs-repeater-embed-container">
                    <div className="docs-repeater-embed-iframe">
                      <div
                        className="docs-repeater-embed-content"
                        dangerouslySetInnerHTML={{ __html: `${doc.Embed} ` }}
                      />
                    </div>
                  </div>
                  <div
                    className="docs-repeater-embed-headline"
                    title={doc.name}
                  >
                    {doc.name}
                  </div>
                  <div
                    className="docs-repeater-embed-strapline"
                    title={doc.description}
                  >
                    {doc.description}
                  </div>
                  <div
                    className="docs-repeater-embed-strapline"
                    title={doc.itemName}
                  >
                    {doc.itemName}
                  </div>
                </div>
              </>
            ) : (
              <Document
                headline={
                  doc.documentType === 'Onboarding'
                    ? doc.image && doc.image.split(S3BucketConfig.url)
                    : doc.name
                }
                strapline={
                  doc.type === 'Video'
                    ? doc.description
                    : doc.type === 'Other'
                      ? 'General'
                      : doc.type
                }
                itemName={doc.itemName}
                image={doc.image ? doc.image : null}
                onDownload={() =>
                  doc.image ? onDownload(doc.image) : onDownload()
                }
                onClick={() => {
                  onClick(
                    index,
                    doc.image ?? doc.document ?? '',
                    isImage(doc.image)
                  );
                }}
                icon={
                  doc.attachmentPreview ? (
                    <Attachments
                      attachments={doc.attachmentPreview}
                      onClickAttachment={() =>
                        onClick(
                          index,
                          doc.image ?? doc.document ?? '',
                          isImage(doc.image)
                        )
                      }
                      fullSize
                    />
                  ) : (
                    <FontAwesomeIcon
                      glyph={
                        ICONS[
                          doc.type === 'General' || doc.type === 'Other'
                            ? 'OtherDoc'
                            : doc.type
                        ] as any
                      }
                      weight="light"
                    />
                  )
                }
                onDelete={
                  effectiveCanDelete ? () => handleDelete(doc) : false
                }
                active={undefined}
                itemTypeWalkthrough={undefined}
                small={undefined}
                onView={undefined}
                check={undefined}
                edit={undefined}
                onEdit={undefined}
              />
            )}
          </div>
        )
      )}
    </div>
    </div>
    {documentToDelete && (
      <ConfirmationModal
        title="Delete Document"
        message="Are you sure you want to delete this document?"
        warningMessage="This action cannot be undone and the document will be permanently removed."
        confirmLabel="Delete Document"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        variant="danger"
        requireTypeToConfirm={true}
        confirmationWord="DELETE"
      />
    )}
    </>
  );
};

export default DocumentsRepeater;
